/**
 * Phase 7 Gelombang 1 — Scenario Detail
 * - Edit assumptions (DRAFT only), calculate, submit, approve/reject, clone
 * - Lihat Perhitungan modal per baris
 */
import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ArrowLeft, Save, Play, Send, Check, X, Copy, BookOpen, History } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import ScenarioAnalysisPanels from "@/components/executive/ScenarioAnalysisPanels";
import ManagementActionsPanel from "@/components/executive/ManagementActionsPanel";

const APPROVE_ROLES = new Set(["super_admin", "direktur_utama"]);
const EDIT_ROLES = new Set(["super_admin", "direktur_utama", "direktur_operasional", "keuangan", "akuntansi"]);

const DRIVER_LABELS = {
  revenue_growth_percent:      "Pertumbuhan Omzet (%)",
  volume_growth_percent:       "Pertumbuhan Volume (%)",
  selling_price_change_percent:"Perubahan Harga Jual (%)",
  gross_margin_percent:        "Gross Margin (%)",
  hpp_change_percent:          "Perubahan HPP (%)",
  opex_change_percent:         "Perubahan OpEx (%)",
  payroll_change_percent:      "Perubahan Payroll (%)",
  ar_days:                     "AR Days",
  ap_days:                     "AP Days",
  inventory_days:              "Inventory Days",
  capex:                       "Belanja Modal (Rp)",
  tax_rate_percent:            "Tarif Pajak (%)",
  financing_cost:              "Biaya Pendanaan (Rp)",
};

const fmt = (x) => (x === null || x === undefined) ? "—" :
  new Intl.NumberFormat("id-ID", { maximumFractionDigits: 2 }).format(x);
const fmtRp = (x) => x === null || x === undefined ? "—" :
  `Rp ${new Intl.NumberFormat("id-ID").format(Math.round(x))}`;

export default function ExecutiveScenarioDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const canEdit = EDIT_ROLES.has(user?.role);
  const canApprove = APPROVE_ROLES.has(user?.role);
  const [sc, setSc] = useState(null);
  const [result, setResult] = useState(null);
  const [versions, setVersions] = useState([]);
  const [traceModal, setTraceModal] = useState(null);
  const [err, setErr] = useState(null);
  const [working, setWorking] = useState(false);

  const load = () => {
    api.get(`/executive/scenarios/${id}`)
      .then(({ data }) => {
        setSc(data);
        setResult(data.latest_result);
      })
      .catch((e) => {
        if (e?.response?.status === 403) setErr("Tidak berwenang.");
        else setErr(formatApiError(e));
      });
    api.get(`/executive/scenarios/${id}/versions`)
      .then(({ data }) => setVersions(data.items || []))
      .catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const updateAssumption = (name, value) => {
    const assumptions = (sc.assumptions || []).map((a) =>
      a.name === name ? { ...a, value: value === "" ? null : Number(value), source: "MANUAL", overridden: true } : a);
    setSc({ ...sc, assumptions });
  };

  const saveDraft = async () => {
    if (sc.status !== "DRAFT") {
      toast.error("Hanya DRAFT yang dapat disimpan. Buat clone untuk versi baru.");
      return;
    }
    setWorking(true);
    try {
      await api.put(`/executive/scenarios/${id}`, {
        name: sc.name, description: sc.description,
        assumptions: (sc.assumptions || []).map((a) => ({
          name: a.name, value: a.value, source: a.source || "MANUAL",
          source_period: a.source_period,
        })),
      });
      toast.success("Draft tersimpan.");
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setWorking(false); }
  };

  const calc = async () => {
    setWorking(true);
    try {
      const { data } = await api.post(`/executive/scenarios/${id}/calculate`);
      setResult(data);
      toast.success("Perhitungan selesai.");
    } catch (e) { toast.error(formatApiError(e)); } finally { setWorking(false); }
  };

  const act = async (action) => {
    let note = "";
    let reason = "";
    if (action === "submit") {
      reason = window.prompt("Alasan pengajuan (minimal 3 karakter):", "Submit untuk review") || "Submit untuk review";
      if (reason.length < 3) reason = "Submit untuk review";
    } else if (action === "approve" || action === "reject") {
      note = window.prompt(`Catatan ${action}:`) || "";
    }
    setWorking(true);
    try {
      const payload = action === "submit" ? { reason } : { decision_note: note };
      await api.post(`/executive/scenarios/${id}/${action}`, payload);
      toast.success(`Aksi ${action} berhasil.`);
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setWorking(false); }
  };

  const clone = async () => {
    try {
      const { data } = await api.post(`/executive/scenarios/${id}/clone`);
      toast.success("Skenario di-clone.");
      window.location.href = `/executive-scenarios/${data.id}`;
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (err) return (
    <div className="p-6 space-y-3" data-testid="exec-scenario-detail-page">
      <Link to="/executive-scenarios" className="text-xs text-sky-700 hover:underline inline-flex items-center">
        <ArrowLeft size={12} className="mr-1" />Kembali
      </Link>
      <div className="rounded-md border border-amber-300 bg-amber-50 p-4 text-amber-900 text-sm" data-testid="exec-scenario-no-access">{err}</div>
    </div>
  );
  if (!sc) return <div className="p-6 text-xs text-slate-400 italic">Memuat…</div>;

  const months = result?.months || [];
  const chartData = months.map((m) => ({
    period: m.period, revenue: m.kpis.revenue, net_profit: m.kpis.net_profit,
    ending_cash: m.kpis.ending_cash, working_capital: m.kpis.working_capital,
  }));

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="exec-scenario-detail-page">
      <Link to="/executive-scenarios" className="text-xs text-sky-700 hover:underline inline-flex items-center">
        <ArrowLeft size={12} className="mr-1" />Daftar Skenario
      </Link>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
        <div>
          <h1 className="font-display text-xl md:text-2xl font-bold text-slate-900" data-testid="sc-detail-name">
            {sc.name} <span className="text-sm text-slate-500">({sc.scenario_type})</span>
          </h1>
          <div className="text-xs text-slate-500">
            Periode {sc.year}-{String(sc.month).padStart(2, "0")} · Horizon {sc.horizon_months} bulan · v{sc.version}
          </div>
          {sc.description && <p className="text-xs text-slate-600 mt-1">{sc.description}</p>}
        </div>
        <div className="flex gap-2 flex-wrap">
          <Badge variant="outline" className="px-2 py-1" data-testid="sc-detail-status">{sc.status}</Badge>
          <Button size="sm" variant="outline" onClick={calc} disabled={working} data-testid="sc-calc">
            <Play size={11} className="mr-1" />Hitung
          </Button>
          {sc.status === "DRAFT" && canEdit && (
            <>
              <Button size="sm" variant="outline" onClick={saveDraft} disabled={working} data-testid="sc-save">
                <Save size={11} className="mr-1" />Simpan Draft
              </Button>
              <Button size="sm" onClick={() => act("submit")} disabled={working} data-testid="sc-submit-act">
                <Send size={11} className="mr-1" />Ajukan
              </Button>
            </>
          )}
          {sc.status === "SUBMITTED" && canApprove && (
            <>
              <Button size="sm" variant="outline" onClick={() => act("approve")} disabled={working} data-testid="sc-approve">
                <Check size={11} className="mr-1" />Setujui + Kunci
              </Button>
              <Button size="sm" variant="outline" onClick={() => act("reject")} disabled={working} data-testid="sc-reject">
                <X size={11} className="mr-1" />Tolak
              </Button>
            </>
          )}
          {canEdit && (
            <Button size="sm" variant="outline" onClick={clone} data-testid="sc-clone">
              <Copy size={11} className="mr-1" />Clone ke Periode Berikut
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Assumptions */}
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Asumsi (Driver)</CardTitle></CardHeader>
          <CardContent className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Driver</TableHead>
                  <TableHead className="text-right">Nilai</TableHead>
                  <TableHead>Sumber</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(sc.assumptions || []).map((a) => (
                  <TableRow key={a.name} data-testid={`asm-${a.name}`}>
                    <TableCell className="text-xs">{DRIVER_LABELS[a.name] || a.name}</TableCell>
                    <TableCell className="text-right">
                      <Input type="number" value={a.value ?? ""}
                             disabled={sc.status !== "DRAFT" || !canEdit}
                             onChange={(e) => updateAssumption(a.name, e.target.value)}
                             className="h-7 text-right text-xs"
                             data-testid={`asm-input-${a.name}`} />
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-[10px]">
                        {a.source}{a.source_period ? ` · ${a.source_period}` : ""}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Totals */}
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Ringkasan Proyeksi {sc.horizon_months} Bulan</CardTitle></CardHeader>
          <CardContent>
            {!result ? (
              <div className="text-xs text-slate-400 italic py-6 text-center" data-testid="sc-no-result">
                Klik <strong>Hitung</strong> untuk memproyeksikan skenario.
              </div>
            ) : !result.has_baseline ? (
              <div className="text-xs text-amber-700 italic py-4" data-testid="sc-no-baseline">
                Data sumber belum tersedia untuk periode baseline — proyeksi dibuat tetapi nilai nol.
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2 text-xs" data-testid="sc-totals">
                {[
                  ["Total Omzet", fmtRp(result.totals.revenue)],
                  ["Gross Profit", fmtRp(result.totals.gross_profit)],
                  ["Gross Margin %", `${result.totals.gross_margin_percent}%`],
                  ["OpEx", fmtRp(result.totals.opex)],
                  ["Net Profit", fmtRp(result.totals.net_profit)],
                  ["Net Margin %", `${result.totals.net_margin_percent}%`],
                  ["Operating Cashflow", fmtRp(result.totals.operating_cashflow)],
                  ["Ending Cash", fmtRp(result.totals.ending_cash)],
                  ["Working Capital (akhir)", fmtRp(result.totals.working_capital_last)],
                  ["Capex", fmtRp(result.totals.capex)],
                ].map(([k, v]) => (
                  <div key={k} className="flex justify-between border-b py-1">
                    <span className="text-slate-500">{k}</span>
                    <span className="font-semibold">{v}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Chart */}
      {chartData.length > 0 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Proyeksi Bulanan</CardTitle></CardHeader>
          <CardContent>
            <div style={{ width: "100%", height: 280, minWidth: 320 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                  <XAxis dataKey="period" fontSize={11} />
                  <YAxis fontSize={10} />
                  <Tooltip formatter={(v) => fmtRp(v)} />
                  <Legend />
                  <Line type="monotone" dataKey="revenue" stroke="#1e3a5f" strokeWidth={2} name="Omzet" />
                  <Line type="monotone" dataKey="net_profit" stroke="#059669" strokeWidth={2} name="Net Profit" />
                  <Line type="monotone" dataKey="ending_cash" stroke="#f59e0b" strokeWidth={2} name="Ending Cash" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Monthly table with Lihat Perhitungan */}
      {months.length > 0 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Rincian Per Bulan</CardTitle></CardHeader>
          <CardContent className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Periode</TableHead>
                  <TableHead className="text-right">Omzet</TableHead>
                  <TableHead className="text-right">HPP</TableHead>
                  <TableHead className="text-right">GP</TableHead>
                  <TableHead className="text-right">OpEx</TableHead>
                  <TableHead className="text-right">Net Profit</TableHead>
                  <TableHead className="text-right">Net CF</TableHead>
                  <TableHead className="text-right">Ending Cash</TableHead>
                  <TableHead className="text-right">WC</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {months.map((m) => (
                  <TableRow key={m.period} data-testid={`mo-${m.period}`}>
                    <TableCell className="font-mono text-xs">{m.period}</TableCell>
                    <TableCell className="text-right text-xs">{fmtRp(m.kpis.revenue)}</TableCell>
                    <TableCell className="text-right text-xs">{fmtRp(m.kpis.hpp)}</TableCell>
                    <TableCell className="text-right text-xs">{fmtRp(m.kpis.gross_profit)}</TableCell>
                    <TableCell className="text-right text-xs">{fmtRp(m.kpis.opex)}</TableCell>
                    <TableCell className="text-right text-xs">{fmtRp(m.kpis.net_profit)}</TableCell>
                    <TableCell className="text-right text-xs">{fmtRp(m.kpis.net_cashflow)}</TableCell>
                    <TableCell className="text-right text-xs">{fmtRp(m.kpis.ending_cash)}</TableCell>
                    <TableCell className="text-right text-xs">{fmtRp(m.kpis.working_capital)}</TableCell>
                    <TableCell>
                      <Button variant="ghost" size="sm" onClick={() => setTraceModal(m)}
                                data-testid={`mo-trace-${m.period}`}>
                        <BookOpen size={11} className="mr-1" />Lihat
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Versions */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <History className="h-4 w-4 text-sky-700" /> Riwayat Versi
          </CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Versi</TableHead>
                <TableHead>Status Saat Snapshot</TableHead>
                <TableHead>Alasan</TableHead>
                <TableHead>Dibuat</TableHead>
                <TableHead>Oleh</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {versions.length === 0 && (
                <TableRow><TableCell colSpan={5} className="text-center text-xs text-slate-400 italic py-4">
                  Belum ada versi
                </TableCell></TableRow>
              )}
              {versions.map((v) => (
                <TableRow key={v.id} data-testid={`ver-${v.id}`}>
                  <TableCell className="text-xs">v{v.version}</TableCell>
                  <TableCell className="text-xs">{v.status}</TableCell>
                  <TableCell className="text-xs">{v.reason}</TableCell>
                  <TableCell className="text-xs">{new Date(v.created_at).toLocaleString("id-ID")}</TableCell>
                  <TableCell className="text-xs">{v.created_by}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Phase 7 Gelombang 2: Sensitivity + Break-even + PDF */}
      <ScenarioAnalysisPanels scenarioId={id} />
      <ManagementActionsPanel scenarioId={id} user={user} />

      {/* Trace Modal */}
      <Dialog open={!!traceModal} onOpenChange={(v) => !v && setTraceModal(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Lihat Perhitungan — {traceModal?.period}</DialogTitle></DialogHeader>
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {(traceModal?.trace || []).map((t, i) => (
              <div key={i} className="border-l-2 border-sky-500 pl-3 py-1" data-testid={`trace-${t.kpi}`}>
                <div className="font-mono text-xs font-bold">{t.kpi}</div>
                <div className="text-[11px] text-slate-600">Formula: <code>{t.formula}</code></div>
                <div className="text-[11px] text-slate-500 mt-1">
                  Input: {Object.entries(t.inputs || {}).map(([k, v]) => `${k}=${fmt(v)}`).join(", ")}
                </div>
                <div className="text-[10px] text-slate-400 mt-0.5">Sumber: {t.source}</div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
